/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */


import { Amount, Course, CourseClass, Preferences, V2PaymentResponse } from '@api';
import { debounce, startCase } from 'lodash'
import moment from 'moment/moment';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { sendAdobeAnalyticsEvent } from 'src/js/actions/AdobeAnalyticActions';
import { IAction } from 'src/js/actions/IshAction';
import { hashSha256 } from 'src/js/common/utils/FormatUtils';
import {
  AdobeAnalyticsCartEvent,
  AdobeAnalyticsCheckoutEventSteps,
  AdobeAnalyticsEvent,
  AdobeAnalyticsLoginEvent,
  AdobeAnalyticsQuickSearchEvents,
  AdobeAnalyticsSearchEvents
} from 'src/js/model/checkout/AdobeAnalytics';
import { ContactState, IshState } from 'src/js/services/IshState';

// Global events handler
declare const window: {
  adobeDataLayer: Array<any>
} & Window & typeof globalThis;

export const sendAAEvent = (event: Record<any, any>) => {
  window.adobeDataLayer = window.adobeDataLayer || [];
  window.adobeDataLayer.push(event);
}

const toAdobeVariable = (input: string | number) => typeof input === 'string' ? input.replace(/^[\n\s]+|[\n\s]+$|[\n\s]{2,}/g,'') : (input || '')

export const courseToAdobeFormat = (course: Course) => ({
  'publishDate': toAdobeVariable(course.publishDate && moment(course.publishDate).format('YYYY-MM-DD')),
  'updateDate': toAdobeVariable(course.updateDate && moment(course.updateDate).format('YYYY-MM-DD')),
  'courseName': toAdobeVariable(course.name),
  'courseCode': toAdobeVariable(course.code),
  'courseFaculty': toAdobeVariable(course.courseFaculty),
  'courseLevel': toAdobeVariable(course.courseLevel),
  'courseDuration': toAdobeVariable(course.courseDuration),
  'courseType': toAdobeVariable(course.courseType),
  'courseDeliveryMode': toAdobeVariable(course.nextClassDeliveryMode),
  'courseAttainment': course.courseAttainment ? `${course.courseAttainment} CPD points` : '',
  'coursePrice': toAdobeVariable(course.classesHighestPrice && parseFloat(course.classesLowestPrice).toFixed(2))
})

export const courseClassToAdobeFormat = (courseClass: CourseClass) => courseToAdobeFormat(courseClass.course)

const toAdobeFilterName = (title: string) => title.toLocaleLowerCase().replace(' ','-');

const getCheckedFilters = () => {
  const filters = []
  document.querySelectorAll<HTMLInputElement>('.filter-option input[type=checkbox]:checked').forEach(f => {
    const title = f.closest('.filter-options')?.querySelector('.filter-options__title')?.textContent;
    const value = f.nextElementSibling?.textContent;
    if (title && value) {
      filters.push(`${toAdobeFilterName(title)}:${toAdobeFilterName(value)}`)
    }
  });
  return filters.toString();
}

const getRank = (courseId: string) => {
  let rank = 0;
  document.querySelectorAll('[data-course-id]').forEach((course: any, index) => {
    if (course.dataset.courseId === courseId) {
      rank = index
    }
  })
  return rank;
}

export const sendPaginationEvent = (paginationNumber) => {
  sendAAEvent({
    'event': 'paginationChange', 
    'search': { 
      'paginationNumber': paginationNumber
    }
  })
}

export const sendSearchEvent = (courses: Course[], params: URLSearchParams, eventType: AdobeAnalyticsSearchEvents, isProduct: boolean, title: string) => {
  const newEvent = {
    'event': eventType,
    'search': {
      'term': toAdobeVariable(params.get('s') || params.get('clickTerm')), //search term//
      'numberOfSearchResults': toAdobeVariable(document.querySelector('.page-search__results-count .js-search-results-count')?.textContent),
      'filterApplied': params.get('searchType') === 'site search' && eventType === 'search'
        ? 'filter by:courses'
        : toAdobeVariable(getCheckedFilters()),
      'componentName': startCase(eventType),
      'type': toAdobeVariable(params.get('searchType') || (document.getElementById('courses-list') ? 'subject area search' : '')),
      ...eventType === 'searchResultClick' ? {
        'paginationNumber': toAdobeVariable(document.querySelector('a.pagination__page.active')?.textContent),
        'searchResultRank': getRank(courses[0]?.id),
        'searchResultTitle': title,
        'isProduct': isProduct?.toString() || 'false'
      } : {}
    },
    'course': courses.length ? courses.map(courseToAdobeFormat) : ''
  }
  if (eventType === 'searchFilter' && !newEvent.search.filterApplied) {
    newEvent.event = 'search'
    newEvent.search.componentName = startCase('search')
  }
  if (params?.get('clickTerm') && history.pushState) {
    const newurl = new URL(location.href);
    newurl.searchParams.delete('clickTerm');
    window.history.pushState({path:newurl.toString()},'',newurl.toString());
  }
  sendAAEvent(newEvent);
}

export const sendQuickSearchEvent = (courses: Course[], params: URLSearchParams, eventType: AdobeAnalyticsQuickSearchEvents, isProduct: boolean, title: string) => {
  const links = document.querySelectorAll('[data-adobe-quicksearch-item]');
  let rank;

  links.forEach((l, index) => {
    if (l.textContent.includes(title)) {
      rank = index + 1;
    }
  })

  const newEvent = {
    'event': eventType,
    'search': {
      'term': toAdobeVariable(document.querySelector<any>('input.quicksearch').value), //search term//
      'numberOfSearchResults': links.length,
      'filterApplied':toAdobeVariable(getCheckedFilters()),
      'componentName': startCase(eventType),
      'type': 'quick search',
      ...eventType === 'quickSearchhResultClick' ? {
        'paginationNumber': toAdobeVariable(document.querySelector('a.pagination__page.active')?.textContent),
        'searchResultRank': toAdobeVariable(rank),
        'searchResultTitle': toAdobeVariable(title),
        'isProduct': isProduct?.toString() || 'false'
      } : {}
    },
    'course': courses.length ? courses.map(courseToAdobeFormat) : ''
  }
  sendAAEvent(newEvent);
}

export const sendPageViewEvent = async (courses: Course[], contact?: ContactState, contactId?: string) => {
  const site = document.location.host.split('.')[0];
  const isCoursePage = document.location.href.includes('/course/');
  const hashedEmail = contact ? await hashSha256(contact.email) : '';
  const isErrorPage = Boolean(document.getElementById('PageNotFound') || document.getElementById('roundedExceptionBox'))

  sendAAEvent(
    {
      'event': 'pageView',
      'page': {
        'name': toAdobeVariable(document.location.pathname === '/' ? site : `${site}${document.location.pathname.replaceAll('/',':')}`),
        'url' : toAdobeVariable(document.location.href),
        'isErrorPage': isErrorPage,
        'site': toAdobeVariable(site),
        'title': toAdobeVariable(document.title),
        'previousPageName': toAdobeVariable(document.referrer?.replace(/https:\/\/|.oncourse.cc/g,'').replace(/\/(?!$)/g,':').replaceAll('/','')),
        'coursePage': isCoursePage && courses.length ? courseToAdobeFormat(courses[0]) : ''
      },
      'course': courses.length
        ? (isCoursePage
          ? (courses.slice(1).length
            ? courses.slice(1).map(courseToAdobeFormat)
            : '')
          : courses.map(courseToAdobeFormat))
        : '',
      'user': contact
        ? {
          'loginStatus': toAdobeVariable('authenticated'),
          'hashedEmail': toAdobeVariable(hashedEmail),
          'userId': toAdobeVariable(contactId),
          'userType': ''
        }
        : {'loginStatus': 'unauthenticated'}

    })
}

export const sendCheckoutEvent = (step: AdobeAnalyticsCheckoutEventSteps, classes: CourseClass[]) => {
  const courses = classes.map(courseClassToAdobeFormat);
  sendAAEvent(
    {
      'event': 'checkout',
      'ecommerce': {
        'checkoutStepName': step,
      },
      'course': courses.length ? courses : ''
    }
  )  
}

export const sendPurchaseEvent = (classes: CourseClass[], amount: Amount, payment: V2PaymentResponse) => {
  const courses = classes.map(courseClassToAdobeFormat);
  sendAAEvent(
    {
      'event': 'purchase',
      'ecommerce': {
        'transactionId': toAdobeVariable(payment.reference),
        'revenue':Number(amount.payNow || 0).toFixed(2),
        'tax': Number(amount.totalTax || 0).toFixed(2),
        'paymentMethod': toAdobeVariable(payment.status === 'SUCCESSFUL_BY_PASS'
          ? 'corporate pass'
          : payment.status === 'SUCCESSFUL' ? 'credit card' : 'zero payment'),
      },
      'course': courses.length ? courses : ''
    }
  )
}

export const sendCartEvent = (type: AdobeAnalyticsCartEvent, courseClass: CourseClass) => {
  sendAAEvent({
    'event': type,
    'course': [courseClassToAdobeFormat(courseClass)]
  })
}


export const sendLoginEvent = (type: AdobeAnalyticsLoginEvent, email: string, userId: string) => {
  hashSha256(email).then(hashedEmail => sendAAEvent(({
    'event': type,
    'user': {
      'loginStatus': type === 'loginFail' ? 'unauthenticated' : 'authenticated',
      'hashedEmail': `${hashedEmail}`,
      'userId': toAdobeVariable(userId)
    }
  })))
}

export const sendFormSubmit = (name: string, type: string) => {
  sendAAEvent({
    'event': 'formSubmit',
    'form': {
      'name': toAdobeVariable(name),
      'type': toAdobeVariable(type),
    }
  })
}

export const sendFormAbandon = (name: string, type: string, lastInteractedField: string) => {
  sendAAEvent({
    'event': 'formAbandon',
    'form': {
      'name': toAdobeVariable(name),
      'type': toAdobeVariable(type),
      'lastInteractedField': toAdobeVariable(lastInteractedField),
    }
  })
}

export const LINKS_SELECTOR = 'a:not([href*="#"])';

class AdobeAnalyticsFormAbandonTrackServiceBase {
  private forms: Set<string> = new Set();

  public dispatch: Dispatch<IAction<any>>;

  private trackFormAbandon = (e) => {
    const { href } = getLinkClickTarget(e);
    if (href) {
      e.preventDefault();
      e.stopPropagation();
      this.dispatch(sendAdobeAnalyticsEvent('formAbandon', { forms: Array.from(this.forms), href }))
    }
  }

  public attachFormAbandonClicksTrack = (form,dispatch) => {
    if (!this.dispatch) {
      this.dispatch = dispatch;
    }
    if (!this.forms.size) {
      document.querySelectorAll(LINKS_SELECTOR).forEach(link => link.addEventListener('click', this.trackFormAbandon, true));
    }
    this.forms.add(form);
  }

  public detachFormAbandonClicksTrack = () => {
    document.querySelectorAll(LINKS_SELECTOR).forEach(link => link.removeEventListener('click', this.trackFormAbandon, true));
    this.forms.clear();
  }
}

export const AdobeAnalyticsFormAbandonTrackService = new AdobeAnalyticsFormAbandonTrackServiceBase();

const getLinkClickTarget = (e) => {
  let target;
  let href;

  if (e.target?.href) {
    target = e.target;
    href = target?.href;
    return { target, href };
  }

  if (e.currentTurget?.href) {
    target = e.currentTurget;
    href = target?.href;
    return { target, href };
  }
  if ((e.target || e.currentTurget)?.closest('a')?.href) {
    target = (e.target || e.currentTurget)?.closest('a');
    href = target?.href;
    return { target, href };
  }

  return { target, href };
}

export function AdobeAnalyticsCheckoutStartTrack() {
  const dispatch = useDispatch<Dispatch<IAction>>();
  const preferences = useSelector<IshState, Preferences>(state => state.preferences);
  const checkoutPath = useSelector<IshState>(state => state.config.checkoutPath);

  const attached = useRef(false);

  const sendEvent = (type: AdobeAnalyticsEvent, event?: Record<any, any>) => dispatch(sendAdobeAnalyticsEvent(type, event));

  const isSearchResultsPage = document.getElementById('courses-list');

  const trackCheckoutStart = (e) => {
    const { href } = getLinkClickTarget(e);
    if (href) {
      e.preventDefault();
      e.stopPropagation();
      sendEvent('start', { href })
    }
  }

  const trackSearchResultClick = (e) => {
    const { href, target } = getLinkClickTarget(e);
    if (href) {
      e.preventDefault();
      e.stopPropagation();
      const courseId = target?.closest('[data-course-id]')?.dataset?.courseId;
      const title = target?.querySelector('.course-card__title')?.textContent;
      sendEvent('searchResultClick', { href, courseId, title })
    }
  }

  const trackPaginationClick = useCallback((e) => {
    const { href, target } = getLinkClickTarget(e);
    if (href) {
      e.preventDefault();
      e.stopPropagation();

      let number = toAdobeVariable(target?.textContent) as string;

      const active = parseInt(toAdobeVariable(document.querySelector('.pagination__page.active')?.textContent) as string)

      if (number.includes('Previous')) {
        number = String(active - 1)
      }

      if (number.includes('Next')) {
        number = String(active + 1)
      }

      sendPaginationEvent(
        number
      )
      target?.removeEventListener('click', trackPaginationClick, true);
      if (typeof target?.click === 'function') target?.click();
    }
  }, [])


  if (isSearchResultsPage && preferences.adobeAnalyticsEnablde) {
    document.querySelectorAll('a.pagination__page').forEach(link =>
      link.addEventListener('click', trackPaginationClick, true)
    )
  }

  const attachCheckoutStartTrack = () => {
    sendEvent('pageView')
    document.querySelectorAll(`a[href*="${checkoutPath}"]`).forEach(link =>
      link.addEventListener('click', trackCheckoutStart, {
        once: true,
        capture: true
      })
    )

    if (isSearchResultsPage) {
      document.querySelectorAll('[data-search-result-type] a').forEach(link =>
        link.addEventListener('click', trackSearchResultClick, true)
      )
    }
  }

  const trackQuickSearchClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    sendEvent('quickSearchhResultClick', {
      coursesId: e.target?.dataset?.adobeQuicksearchItemId,
      href: e.target?.href,
      title: e.target?.textContent
    });
  }

  const trackQuickSearch = useCallback(debounce(() => {
    const coursesIds = []

    document.querySelectorAll<any>('[data-adobe-quicksearch-item-id]').forEach(item => {
      if (item.dataset?.adobeQuicksearchItem === 'Course') {
        coursesIds.push(item.dataset?.adobeQuicksearchItemId)
      }
    })

    sendEvent('quickSearch', { coursesIds });

    document.querySelectorAll('[data-adobe-quicksearch-item]').forEach(link => {
      link.addEventListener('click', trackQuickSearchClick);
    })

  }, 1000), [])

  useEffect(() => {
    if (preferences.adobeAnalyticsEnablde && !attached.current) {
      attached.current = true
      attachCheckoutStartTrack();
      document.addEventListener('ish:quicksearchLoaded', trackQuickSearch)
    }
    return () => {
      document.removeEventListener('ish:quicksearchLoaded', trackQuickSearch)
    }
  }, [preferences.adobeAnalyticsEnablde, checkoutPath])

  return undefined;
}

export const ADOBE_REACT_ID = 'AdobeAnalyticsReactTrack';